import { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react'
import type { TrackingList } from '../types/finance'
import { getQuote, isUsingMockData } from './finance-api'

// Unified watchlists: Favorites is just the default tracking list (id
// 'favorites', non-deletable). Every list — favorites included — persists to
// localStorage, fixing the old favorites feature that kept stars in memory
// only and reset to defaults on each visit.

export const FAVORITES_LIST_ID = 'favorites'
const STORAGE_KEY = 'nb-equity-tracking-lists'
const ACTIVE_KEY = 'nb-active-tracking-list'
const DEFAULT_FAVORITES = ['AAPL', 'MSFT', 'GOOGL']

export interface WatchQuote {
  name: string
  price: number
  change: number
  changePercent: number
  trailingBeta: number | null
}

function makeFavoritesList(symbols: string[]): TrackingList {
  return { id: FAVORITES_LIST_ID, name: 'Favorites', symbols, createdAt: new Date().toISOString() }
}

/** Read persisted lists, guaranteeing Favorites exists at position 0. */
function readLists(): TrackingList[] {
  let stored: TrackingList[] = []
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as TrackingList[]
      if (Array.isArray(parsed)) {
        stored = parsed.filter((l) => l && typeof l.id === 'string' && Array.isArray(l.symbols))
      }
    }
  } catch {
    stored = []
  }
  const favorites = stored.find((l) => l.id === FAVORITES_LIST_ID)
  const others = stored.filter((l) => l.id !== FAVORITES_LIST_ID)
  // First run (no favorites list yet): seed with the old default favorites.
  return [favorites ?? makeFavoritesList(DEFAULT_FAVORITES), ...others]
}

function writeLists(lists: TrackingList[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lists))
  } catch {
    // Storage unavailable — lists won't persist this session.
  }
}

function newListId(): string {
  return `list-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`
}

interface WatchlistsContextType {
  lists: TrackingList[]
  activeListId: string
  activeList: TrackingList
  setActiveListId: (id: string) => void
  /** symbol → hydrated quote (shared across lists) */
  quotes: Record<string, WatchQuote>
  /** symbols with a quote fetch in flight */
  pending: Set<string>
  isMock: boolean
  createList: (name: string) => string | null
  renameList: (id: string, name: string) => void
  deleteList: (id: string) => void
  addSymbol: (listId: string, symbol: string) => void
  removeSymbol: (listId: string, symbol: string) => void
  isInList: (listId: string, symbol: string) => boolean
  isFavorited: (symbol: string) => boolean
  toggleFavorite: (symbol: string) => void
}

const WatchlistsContext = createContext<WatchlistsContextType | null>(null)

export function WatchlistsProvider({ children }: { children: React.ReactNode }) {
  const [lists, setLists] = useState<TrackingList[]>(() => readLists())
  const [activeListId, setActiveListIdState] = useState<string>(() => {
    try {
      return localStorage.getItem(ACTIVE_KEY) ?? FAVORITES_LIST_ID
    } catch {
      return FAVORITES_LIST_ID
    }
  })
  const [quotes, setQuotes] = useState<Record<string, WatchQuote>>({})
  const [pending, setPending] = useState<Set<string>>(new Set())
  const [isMock, setIsMock] = useState(false)
  const quotesRef = useRef(quotes)
  quotesRef.current = quotes
  const pendingRef = useRef(pending)
  pendingRef.current = pending

  const activeList = lists.find((l) => l.id === activeListId) ?? lists[0]

  // Persist lists on every change — the whole point of the unification.
  useEffect(() => {
    writeLists(lists)
  }, [lists])

  const setActiveListId = useCallback((id: string) => {
    setActiveListIdState(id)
    try {
      localStorage.setItem(ACTIVE_KEY, id)
    } catch {
      // non-fatal
    }
  }, [])

  const hydrate = useCallback((symbols: string[]) => {
    const missing = symbols.filter((s) => !quotesRef.current[s] && !pendingRef.current.has(s))
    if (missing.length === 0) return
    setPending((prev) => new Set([...prev, ...missing]))
    missing.forEach(async (sym) => {
      try {
        const q = await getQuote(sym)
        setQuotes((prev) => ({
          ...prev,
          [sym]: {
            name: q.name,
            price: q.currentPrice,
            change: q.change,
            changePercent: q.changePercent,
            trailingBeta: q.trailingBeta,
          },
        }))
        setIsMock(isUsingMockData())
      } catch {
        // Row renders un-hydrated; symbol still shows.
      } finally {
        setPending((prev) => {
          const next = new Set(prev)
          next.delete(sym)
          return next
        })
      }
    })
  }, [])

  // Hydrate quotes for whichever list is on screen.
  useEffect(() => {
    if (activeList) hydrate(activeList.symbols)
  }, [activeList, hydrate])

  const createList = useCallback((name: string): string | null => {
    const trimmed = name.trim()
    if (!trimmed) return null
    const list: TrackingList = { id: newListId(), name: trimmed, symbols: [], createdAt: new Date().toISOString() }
    setLists((prev) => [...prev, list])
    return list.id
  }, [])

  const renameList = useCallback((id: string, name: string) => {
    const trimmed = name.trim()
    if (!trimmed || id === FAVORITES_LIST_ID) return
    setLists((prev) => prev.map((l) => (l.id === id ? { ...l, name: trimmed } : l)))
  }, [])

  const deleteList = useCallback(
    (id: string) => {
      if (id === FAVORITES_LIST_ID) return
      setLists((prev) => prev.filter((l) => l.id !== id))
      if (activeListId === id) setActiveListId(FAVORITES_LIST_ID)
    },
    [activeListId, setActiveListId]
  )

  const addSymbol = useCallback(
    (listId: string, symbolRaw: string) => {
      const symbol = symbolRaw.trim().toUpperCase()
      if (!symbol) return
      setLists((prev) =>
        prev.map((l) => (l.id === listId && !l.symbols.includes(symbol) ? { ...l, symbols: [...l.symbols, symbol] } : l))
      )
      hydrate([symbol])
    },
    [hydrate]
  )

  const removeSymbol = useCallback((listId: string, symbol: string) => {
    setLists((prev) => prev.map((l) => (l.id === listId ? { ...l, symbols: l.symbols.filter((s) => s !== symbol) } : l)))
  }, [])

  const isInList = useCallback(
    (listId: string, symbol: string) =>
      lists.some((l) => l.id === listId && l.symbols.includes(symbol.toUpperCase())),
    [lists]
  )

  const isFavorited = useCallback(
    (symbol: string) => isInList(FAVORITES_LIST_ID, symbol),
    [isInList]
  )

  const toggleFavorite = useCallback(
    (symbol: string) => {
      const sym = symbol.toUpperCase()
      if (isFavorited(sym)) removeSymbol(FAVORITES_LIST_ID, sym)
      else addSymbol(FAVORITES_LIST_ID, sym)
    },
    [isFavorited, removeSymbol, addSymbol]
  )

  const value = useMemo<WatchlistsContextType>(
    () => ({
      lists,
      activeListId: activeList?.id ?? FAVORITES_LIST_ID,
      activeList,
      setActiveListId,
      quotes,
      pending,
      isMock,
      createList,
      renameList,
      deleteList,
      addSymbol,
      removeSymbol,
      isInList,
      isFavorited,
      toggleFavorite,
    }),
    [lists, activeList, setActiveListId, quotes, pending, isMock, createList, renameList, deleteList, addSymbol, removeSymbol, isInList, isFavorited, toggleFavorite]
  )

  return <WatchlistsContext.Provider value={value}>{children}</WatchlistsContext.Provider>
}

export function useWatchlists() {
  const ctx = useContext(WatchlistsContext)
  if (!ctx) throw new Error('useWatchlists must be used within WatchlistsProvider')
  return ctx
}
